import * as SelectPrimitive from '@radix-ui/react-select';
import type { VariantProps } from 'class-variance-authority';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React from 'react';
import {
  SelectLabelVariants,
  SelectMessageVariants,
  SelectTriggerVariants,
} from './select.variants';
import { cn } from '@/shared/lib/cn';

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Value
    ref={ref}
    className={cn('text-[0.875rem]! text-input-text', className)}
    {...props}
  />
));
SelectValue.displayName = SelectPrimitive.Value.displayName;

export interface SelectTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    VariantProps<typeof SelectTriggerVariants> {}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, outline, size, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(SelectTriggerVariants({ size, outline }), 'group', className)}
    {...props}
  >
    {children}

    <SelectPrimitive.Icon asChild>
      <ChevronDown
        className="
        h-4 w-4
        opacity-60
        transition-transform
        duration-300
        ease-in-out
        group-data-[state=open]:rotate-180
      "
      />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));

SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));

SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      className={cn(
        `
        relative z-50
        max-h-96
        min-w-[8rem]

        overflow-hidden
        rounded-md
        border
        border-outline

        bg-select-background

        text-[0.875rem]
        shadow-md
        `,
        className,
      )}
      {...props}
    >
      <SelectScrollUpButton />

      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' && 'w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>

      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));

SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      `
      relative
      flex
      items-center

      w-full
      cursor-pointer
      select-none

      rounded-md
      px-3 py-2

      text-[0.875rem]
      outline-none
      transition-colors

      hover:bg-prominent

      data-[disabled]:opacity-50
      data-[disabled]:pointer-events-none
      `,
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));

SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn('my-1 h-px', className)} {...props} />
));

SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export interface SelectInputProps extends VariantProps<typeof SelectTriggerVariants> {
  label?: string;
  placeholder?: string;
  value?: string | null;
  onValueChange?: (value: string) => void;
  onClear?: () => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  helperText?: string;
  error?: string;
  icon?: React.ReactNode;
  clearable?: boolean;
  emptyLabel?: string;
  'data-testid'?: string;
}

const SelectInput = React.forwardRef<React.ElementRef<typeof SelectTrigger>, SelectInputProps>(
  (
    {
      className,
      outline,
      label,
      placeholder = 'Seleccionar',
      children,
      value,
      onValueChange,
      helperText,
      error,
      disabled,
      size,
      icon,
      clearable = true,
      emptyLabel = 'Sin opciones disponibles',
      'data-testid': dataTestId,
    },
    ref,
  ) => {
    const hasValue = value && value.length > 0;

    return (
      <div className="flex flex-col w-full" data-testid={dataTestId}>
        <div
          className="
              relative
              flex
              items-center
              rounded-[0.5rem]
            "
        >
          {label && <label className={SelectLabelVariants()}>{label}</label>}
          {icon && (
            <span className="absolute left-[0.625rem] flex items-center text-[--color-primary]">
              {icon}
            </span>
          )}
          <Select
            value={value ?? undefined}
            // Radix re-emits through onValueChange when the controlled value changes while the
            // select is closed — e.g. a form.reset() once async detail data lands — and what it
            // emits is ''. No SelectItem can hold an empty value, so an empty emission is never a
            // real selection; forwarding it would write '' back into the form and blank the field.
            onValueChange={(next) => {
              if (next) onValueChange?.(next);
            }}
            disabled={disabled}
          >
            <SelectTrigger
              ref={ref}
              size={size}
              className={cn(className, icon && 'pl-10', clearable && hasValue && 'pr-3')}
              outline={outline}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {React.Children.count(children) === 0 ? (
                <div className="px-3 py-2 text-[0.875rem] text-welcome-subtitle select-none">
                  {emptyLabel}
                </div>
              ) : (
                children
              )}
            </SelectContent>
          </Select>
        </div>

        {(helperText || error) && (
          <span className={SelectMessageVariants({ variant: error ? 'error' : 'helper' })}>
            {error ?? helperText}
          </span>
        )}
      </div>
    );
  },
);

SelectInput.displayName = 'SelectInput';

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SelectInput,
};
